import React from "react";
import { ThemeProvider } from "styled-components";
import Cookies from "universal-cookie";
import { alertActions } from "../alert/Actions";
import { history, store } from "../common/helpers";
import { getTokenCookie } from "../common/helpers/auth-header";
import { MixpanelContext } from "../common/mixpanel";
import { constants } from "../common/ui/constants";
import {
  debounce,
  ResponsiveContext,
  ThemeContext,
  themes,
} from "../common/ui/themes";
import {
  responsiveConstants,
  themeConstants,
} from "../common/ui/themes/Constants";
import themesConstantsBase from "../common/ui/themes/styles/Base";
import { Mixpanel } from "../common/utils";
import { getCompanyInfo } from "../common/utils/actions";
import { SocketContext } from "../common/websocket";
import { ChangePassword } from "../home/changePassword";
import FeatureToggle from "../home/featureToggle";
import Header from "../home/header";
import MyNotificationWrapper from "../home/myNotifications/MyNotificationWrapper";
import Main from "../main";
import { getFeatures } from "./actions";
import { encryptData } from "../common/utils/jsUtils";
import i18n from "./i18n";
import { I18nextProvider } from 'react-i18next';
import { homeServices } from "../home/services";
import { SopTasksProvider } from "../sops/SopTasksContext";
import { userActions } from "../user/Actions";
import { UIWidgetsDiv } from "../common/ui/widgets/division/Division";

const cookies = new Cookies();
const ActivationPopUp = React.lazy(() => import("../home/activationPop/showActivationPopUp"));
const GiveFeedback = React.lazy(() => import("../home/giveFeedback"));
const EditProfilePopUpWrapper = React.lazy(() => import("../home/editProfileModal/editProfileWrapper"));
const SopBackgroundPanel = React.lazy(() => import("../sops/SopBackgroundPanel"));

//below code can only be used if using react app as standalone application
// injectGlobal`

// `;

class App extends React.Component {
  constructor(props) {
    super(props);
    const { dispatch } = this.props;
    const userToken = getTokenCookie();
    const loginState = store.getState()?.login || {};
    const currentUser = loginState.user;
    const shouldHydrateAuth = !!userToken && !currentUser;

    this.state = {
      themeName: themeConstants.THEME_BASE,
      styles: { ...themesConstantsBase, ...constants },
      screenFormatId: responsiveConstants.DESKTOP,
      toggleTheme: this.toggleTheme,
      constants: constants,
      authHydrationPending: shouldHydrateAuth,
      mixpanel: undefined,
      socket: null,
    };
    history.listen(({ location, action }) => {
      dispatch(alertActions.clear());
    });
    if (history.location.pathname !== window.location.pathname) {
      history.push(window.location.pathname);
    }
  }
  toggleTheme = () => {
    this.setState((state) => ({
      themeName:
        state.themeName === themeConstants.THEME_BASE
          ? themeConstants.THEME_ACCELIX
          : themeConstants.THEME_BASE,
      styles: {
        ...themes(
          state.themeName === themeConstants.THEME_BASE
            ? themeConstants.THEME_ACCELIX
            : themeConstants.THEME_BASE
        ),
      },
    }));
  };

  windowDidResize = () => {
    let w = window.innerWidth;
    let formatId;
    if (w < 576) formatId = responsiveConstants.NARROW_MOBILE;
    else if (w < 768) formatId = responsiveConstants.WIDE_MOBILE;
    else if (w < 1024) formatId = responsiveConstants.NARROW_TABLET;
    else if (w < 1280) formatId = responsiveConstants.WIDE_TABLET;
    else formatId = responsiveConstants.DESKTOP;
    if (formatId !== this.state.screenFormatId) {
      this.changeScreen(formatId);
    }
  };

  changeScreen = debounce((formatId) => {
    this.setState({ screenFormatId: formatId });
  }, 250);

  componentCleanup = () => {
    window.removeEventListener("resize", this.windowDidResize);
    const { socket } = this.state;
    if (socket) {
      socket.emit("disconnectSocket", (response) => {
        socket.disconnect();
        console.log(response);
      });
    }
  };

  componentDidMount() {
    if (this.state.authHydrationPending) {
      Promise.resolve(this.props.dispatch(userActions.userData())).finally(() => {
        this.setState({ authHydrationPending: false });
      });
    }

    getFeatures().then(
      (response) => {
        localStorage.setItem(
          "featuresToggles",
          encryptData(JSON.stringify(response.features))
        );
        this.setState({ features: response.features });
        const mixpanelObj = response.features.filter((item) => item.feature === "Mixpanel 2.0");
        if (mixpanelObj.length) {
          const isV2 = mixpanelObj[0].isVisible;
          const mp = Mixpanel.initiateMixpanel("", isV2);
          this.setState({ mixpanel: mp });
        }
      },
      (reject) => {
        console.log("No features");
        this.setState({ isLoading: false });
      }
    );
    let userObj = store.getState("user").login.user;
    if (typeof userObj === "string") userObj = JSON.parse(userObj);
    let user =
      userObj && userObj.user instanceof Array && userObj.user[0]
        ? userObj.user[0]
        : userObj && !(userObj.user instanceof Array)
          ? userObj.user
          : {};
    if (user && user.emailAddr) {
      let email = user.emailAddr.toLowerCase();
      getCompanyInfo(email).then((response) => {
        const details = response?.details || {};
        let people = {
          "Company Address": details.companyAddr || "",
          "Phone Number": details.phoneNum || "",
          $email: email,
          $first_name: user.firstName,
          $last_name: user.lastName,
          "User ID": user.userAccountId,
          "Product ID": "FC Webapp",
          "Last Sign In": new Date().toISOString(),
        };
        cookies.set("people", people);
      }).catch((error) => {
        console.warn("Company information is unavailable:", error);
      });
    }
    homeServices.getBuildVersion()
      .then((versions) => {
        if (Array.isArray(versions) && versions.length > 0) {
          const { build_no } = versions[0];
          cookies.set("buildVersion", build_no);
        } else {
          console.warn("build_version.json is empty or not an array:", versions);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch build version:", error);
      });
    window.addEventListener("pagehide", this.componentCleanup);
    this.windowDidResize();
    window.addEventListener("resize", this.windowDidResize);
    import("socket.io-client").then(({ default: socketIOClient }) => {
      const socket = socketIOClient(window.location.origin, { path: '/ws' });
      this.setState({ socket });
    });
  }

  componentWillUnmount() {
    window.removeEventListener("pagehide", this.componentCleanup);
    window.removeEventListener("resize", this.windowDidResize);
  }
  render() {
    if (this.state.authHydrationPending) {
      return <UIWidgetsDiv loading />;
    }

    const { mixpanel, socket } = this.state;
    const themeContext = {
      themeName: this.state.themeName,
      styles: this.state.styles,
      toggleTheme: this.state.toggleTheme,
    };
    const { screenFormatId } = this.state;
    const theme = this.state.styles;
    const { MainComponent, ...rest } = this.props;
    const data = { ...rest };
    const isAdminPortal = window.location.pathname.includes("admin");

    return (
      <SopTasksProvider>
      <div style={{ height: "100vh" }}>
        <MixpanelContext.Provider value={mixpanel}>
          <SocketContext.Provider value={socket}>
            <ThemeContext.Provider value={themeContext}>
              <ResponsiveContext.Provider value={screenFormatId}>
                <ThemeProvider theme={theme}>
                  <I18nextProvider i18n={i18n}>
                    {!isAdminPortal ? <Header /> : ""}
                    <Main MainComponent={MainComponent} data={data} />
                    <FeatureToggle />
                    <MyNotificationWrapper />
                    <React.Suspense fallback={null}>
                      <ActivationPopUp />
                      <EditProfilePopUpWrapper />
                      <ChangePassword />
                      <GiveFeedback />
                      <SopBackgroundPanel />
                    </React.Suspense>
                  </I18nextProvider>
                </ThemeProvider>
              </ResponsiveContext.Provider>
            </ThemeContext.Provider>
          </SocketContext.Provider>
        </MixpanelContext.Provider>
      </div>
      </SopTasksProvider>
    );
  }
}

export default App;
